import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider, useAuth } from './auth';
import { AppLayout, RequireAuth } from './components/Layout';
import { PageLoader, ToastProvider } from './components/ui';
import { AccountPage } from './pages/Account';
import { AssistantPage } from './pages/Assistant';
import { LoginPage, RegisterPage } from './pages/Auth';
import { BuilderPage } from './pages/Builder';
import { FlowPage } from './pages/FlowPage';
import { NewSurveyPage } from './pages/NewSurvey';
import { PublicSurveyPage } from './pages/PublicSurvey';
import { ResponsesPage } from './pages/Responses';
import { ProgressPage } from './pages/Progress';
import { FieldWorkPage } from './pages/FieldWork';
import { SurveyActivityPage } from './pages/SurveyActivity';
import { SurveyInvitesPage } from './pages/SurveyInvites';
import { SurveysPage } from './pages/Surveys';
import { UsersPage } from './pages/Users';
import { RolesPage } from './pages/Roles';
import { AuditPage } from './pages/Audit';
import { InvitationsPage } from './pages/Invitations';
import { SettingsPage } from './pages/Settings';
import { RespondentsPage } from './pages/Respondents';
import { syncQueuedResponses } from './offlineSync';
import { useI18n } from './i18n';
import './styles/global.css';
import './theme';

function Home() {
  const { user, loading, can } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  // Roles without questionnaire access (respondents, enumerators) have no workspace; send them to their account page.
  return <Navigate to={can('survey.view') ? '/surveys' : '/account'} replace />;
}

/**
 * Builds the tree inside a component that subscribes to the locale, so a
 * language switch re-renders every screen (including date/number formatting).
 */
function App() {
  useI18n();
  useEffect(() => {
    const sync = () => void syncQueuedResponses();
    window.addEventListener('online', sync);
    window.addEventListener('yellowmetrics-auth-change', sync);
    document.addEventListener('visibilitychange', sync);
    sync();
    return () => {
      window.removeEventListener('online', sync);
      window.removeEventListener('yellowmetrics-auth-change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/s/:code" element={<PublicSurveyPage />} />
            <Route
              element={
                <RequireAuth>
                  <AppLayout />
                </RequireAuth>
              }
            >
              <Route path="/" element={<Home />} />
              <Route path="/surveys" element={<SurveysPage />} />
              <Route path="/surveys/new" element={<NewSurveyPage />} />
              <Route path="/surveys/:id" element={<BuilderPage />} />
              <Route path="/surveys/:id/flow" element={<FlowPage />} />
              <Route path="/surveys/:id/:tab" element={<BuilderPage />} />
              <Route path="/surveys/:id/responses" element={<ResponsesPage />} />
              <Route path="/surveys/:id/progress" element={<ProgressPage />} />
              <Route path="/surveys/:id/assignments" element={<FieldWorkPage />} />
              <Route path="/surveys/:id/activity" element={<SurveyActivityPage />} />
              <Route path="/surveys/:id/invites" element={<SurveyInvitesPage />} />
              <Route path="/admin/users" element={<UsersPage />} />
              <Route path="/admin/roles" element={<RolesPage />} />
              <Route path="/admin/invitations" element={<InvitationsPage />} />
              <Route path="/admin/audit" element={<AuditPage />} />
              <Route path="/admin/settings" element={<SettingsPage />} />
              <Route path="/respondents" element={<RespondentsPage />} />
              <Route path="/account" element={<AccountPage />} />
              <Route path="/assistant" element={<AssistantPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
);

if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}
